import React from "react"
import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Paper,
  TableSortLabel,
  Link,
} from "@material-ui/core"
import {
  StyledTableCell,
  StyledTableRow,
} from "components/table"
import { SkeletonTableRowStickyLeftRight } from "components/skeleton/skeleton.component"
import { TablePaginationActions } from "components/tablePagination"
import RefreshTable from "components/table/refreshTable"
import moment from "moment"
import { KeyboardArrowDown } from "@material-ui/icons"
import DefaultImg from "assets/img/img-default.png"
import { getLang } from "app/feature/constants"
import clsx from "clsx"
import "moment/min/locales.min";

const useStyle = makeStyles((theme) => ({
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      borderRight: "1px solid rgba(58, 77, 84, 0.1)",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 37,
      borderRight: "0px",
    },
    "& th th": {
      borderBottom: "none !important",
    },
    "& td td": {
      borderBottom: "none !important",
    },
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
  },
  ellipsisText: {
    maxWidth: "18em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.75rem"
  },
  productPhoto: {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #E4E7EC",
    width: 24,
    height: 24,
    borderRadius: 4,
    marginRight: "10px", 
    "& img": {
      height: 24,
      width: 24,
      borderRadius: 4
    },
  },
  inProgress: {
    color: "#FDB022"
  },
  completed: {
    color: "#32D583"
  },
  rejected: {
    color: "#F97066"
  },
}))

export default function DealerRewardListTableComponent({
  hasStockflowRewardCenterViewAccessRight,
  rows,
  isFetching,
  isError,
  handleReload,
  page,
  language,
  rowsPerPage,
  handleChangePage,
  handleChangeRowPerPage,
  totalFiltered,
  paginationOptions,
  tableFilter,
  changeOrder,
  lang,
  handleViewDetail
}) {
  const classes = useStyle()

  if(language==="ZH")
  {
    moment.locale('zh-cn')
  }else if(language==="TL")
  {
    moment.locale('tl-ph')
  }else
  {
    moment.locale(language)
  }

  return (
    <>
      {!isFetching && isError && <RefreshTable handleReload={handleReload} />}
      {isFetching || rows.length > 0 ? (
        <Paper id="test"style={{overflowX: 'auto', width: "100%"}}>
          <TableContainer>
            <Table
              className={classes.table}
              aria-label="reward table"
              size="small"
              stickyHeader
            >
              <TableHead style={{ height: "45px" }}>
                <TableRow>
                  <StyledTableCell>{getLang(lang, "label.ORDER_ID")}</StyledTableCell>
                  <StyledTableCell>{getLang(lang, "label.REWARD")}</StyledTableCell>
                  <StyledTableCell>{getLang(lang, "label.STATUS")}</StyledTableCell>
                  <StyledTableCell>
                    <TableSortLabel
                      active={tableFilter.orderBy === 'created_at'}
                      direction={tableFilter.order}
                      onClick={() => {
                        const differentTab = tableFilter.orderBy !== "created_at"
                        const order = differentTab ? tableFilter.order : tableFilter.order === 'asc' ? "desc" : "asc"
                        changeOrder("created_at", order)
                      }}
                      IconComponent={KeyboardArrowDown}
                    >
                      {getLang(lang, "label.REDEMPTION_DATE")}
                    </TableSortLabel>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching
                  ? [...new Array(5)].map((v, index) => (
                    <SkeletonTableRowStickyLeftRight 
                      key={index} 
                      columnCount={4} 
                    />
                  ))
                  : rows.map((row, index) => {
                    return (
                      <StyledTableRow
                        key={row.id}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                      >
                        <StyledTableCell>
                          {hasStockflowRewardCenterViewAccessRight ? (
                            <Link
                              color="secondary"
                              component="button"
                              onClick={() => {
                                handleViewDetail(row.uuid)
                              }}
                            >
                              <Typography className={classes.ellipsisText}>{row.reference_code}</Typography>
                            </Link>
                          ) : (
                            <Typography className={classes.ellipsisText}>{row.reference_code}</Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box display="flex" alignItems="center">
                            <Box className={classes.productPhoto}>
                              {row.cover_image_url === "" ? (
                                <img src={DefaultImg} alt="emptyProduct" />
                              ) : (
                                <img src={row.cover_image_url} alt={row.name} />
                              )}
                            </Box>
                            {row.name}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <span 
                            className={clsx({
                              [classes.inProgress]: row.status === "In Progress",
                              [classes.rejected]: row.status === "Rejected",
                              [classes.completed]: row.status === "Completed"
                            })}
                          >
                            {row.status === "Rejected"
                                ? getLang(lang, "label.REJECTED")
                                : row.status === "Completed"
                                ? getLang(lang, "label.COMPLETED")
                                : getLang(lang, "label.IN_PROGRESS")}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell>
                          <span>
                            {moment.utc(row.redemption_date).local().format('D MMM YYYY, HH:mm')}
                          </span>
                        </StyledTableCell>
                      </StyledTableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            style={{
              backgroundColor: "#fff",
              boxShadow: "0px -1px 0px rgba(58, 77, 84, 0.15)",
            }}
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={totalFiltered}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowPerPage}
            labelRowsPerPage={getLang(lang, 'label.ROWS_PER_PAGE')}
            labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString("en-US")}-${to.toLocaleString("en-US")} ${getLang(lang, 'label.OF')} ${count.toLocaleString("en-US")}`}
          />
        </Paper>
      ) : null}
      {!isFetching && !isError && rows.length === 0 && (
        <Box>
          <Table
            className={classes.table}
            aria-label="reward table"
            size="small"
            stickyHeader
          >
            <TableHead style={{ height: "45px" }}>
              <TableRow>
                <StyledTableCell>{getLang(lang, "label.ORDER_ID")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.REWARD")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.STATUS")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.CREATED_AT")}</StyledTableCell>
              </TableRow>
            </TableHead>
          </Table>
          <Box py={9} className={classes.emptyContainer}>
            <Typography variant="body1" style={{ paddingBottom: "16px", fontWeight: "bold" }}>
              {getLang(lang, "message.info.NO_REDEMPTION_EXIST")}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  )
}
